/**
 * Governed agent platform: the agent builder (Builders and Consumers) and the
 * admin page (entitlements, web-access requests, agents, budgets, audit).
 * Models and routing are never configurable here; the model that handled a
 * reply is shown read-only.
 */
const en = {
  'nav.agentBuilder': 'Agent Builder',
  'nav.agentPlatform': 'Agent Platform',

  // Builder page
  'ap.title': 'Agent Builder',
  'ap.subtitle': 'Build agents from instructions and approved tools. The platform picks the model and enforces your permissions.',
  'ap.loadFailed': "Couldn't load your agents.",
  'ap.new': 'New agent',
  'ap.mine': 'My agents',
  'ap.shared': 'Shared with me',
  'ap.empty': 'No agents yet.',
  'ap.loading': 'Loading the Agent Builder…',
  'ap.noAccess': "The Agent Builder isn't enabled for your account. Ask an administrator for access.",

  // Users admin page: per-user grant
  'ap.admin.section': 'Agent Builder',
  'ap.admin.enable': 'Allow this user to build and run agents',
  'ap.admin.hint': 'Off by default. Applies on their next request, including runs already in progress.',
  'ap.admin.adminNote': 'Admins always have access to the Agent Builder.',
  'ap.admin.updateFailed': "Couldn't update Agent Builder access.",
  'ap.disabled': 'Disabled by an admin',
  'ap.version': 'v{n}',

  // Editor
  'ap.edit.open': 'Edit',
  'ap.edit.name': 'Name',
  'ap.edit.instructions': 'Instructions',
  'ap.edit.instructionsHint': 'What the agent should do and how. Models, limits and permissions are set by the platform.',
  'ap.edit.tools': 'Tools',
  'ap.edit.toolsHint': 'Only admin-approved tools you have access to can be attached.',
  'ap.edit.noTools': 'No approved tools are available yet.',
  'ap.edit.unavailable': 'Not granted to you',
  'ap.edit.web': 'Web',
  'ap.edit.writes': 'Changes data',
  'ap.edit.agents': 'Other agents it may call',
  'ap.edit.share': 'Share with my department',
  'ap.edit.memory': 'Remember conversations',
  'ap.edit.retention': 'Keep for (days)',
  'ap.edit.save': 'Save',
  'ap.edit.saving': 'Saving…',
  'ap.edit.cancel': 'Cancel',
  'ap.edit.saved': 'Saved as version {n}.',
  'ap.edit.rejected': 'The platform rejected this agent:',
  'ap.edit.versions': 'Versions',
  'ap.edit.rollback': 'Restore',

  // Run
  'ap.run.placeholder': 'Message the agent…',
  'ap.run.send': 'Send',
  'ap.run.sending': 'Working…',
  'ap.run.handledBy': 'Handled by {model}',
  'ap.run.usedWeb': 'Used web content',
  'ap.run.failed': 'The agent could not answer.',

  // Web access
  'ap.web.have': 'You have web access.',
  'ap.web.none': "You don't have web access.",
  'ap.web.request': 'Request web access',
  'ap.web.reason': 'Why do you need it?',
  'ap.web.pending': 'Your request is waiting for an admin.',
  'ap.web.sent': 'Request sent.',

  // Admin page
  'apa.title': 'Agent Platform',
  'apa.subtitle': 'Grant access, approve web requests, pause agents, set budgets and review the audit trail. Models and routing are changed by platform operators only.',
  'apa.tab.requests': 'Web requests',
  'apa.tab.entitlements': 'Entitlements',
  'apa.tab.agents': 'Agents',
  'apa.tab.budgets': 'Budgets',
  'apa.tab.audit': 'Audit',
  'apa.tab.usage': 'Usage',
  'apa.tab.domains': 'Web domains',
  'apa.domainsDesc': "Narrow web search for one department. This can only narrow the platform's list, never widen it.",
  'apa.domainsInclude': 'Only these domains (one per line; empty = any allowed by the platform)',
  'apa.domainsExclude': 'Never these domains (one per line)',
  'apa.domainsLoad': 'Load',
  'apa.domainsSave': 'Save domains',
  'apa.domainsSaved': 'Saved.',
  'apa.loadFailed': "Couldn't load this section.",
  'apa.colUser': 'User',
  'apa.colReason': 'Reason',
  'apa.colWhen': 'When',
  'apa.colStatus': 'Status',
  'apa.approve': 'Approve',
  'apa.reject': 'Reject',
  'apa.noRequests': 'No pending requests.',
  'apa.grant': 'Grant',
  'apa.revoke': 'Revoke',
  'apa.entName': 'Entitlement',
  'apa.entTarget': 'User or department',
  'apa.entUserId': 'User ID',
  'apa.entDeptId': 'Department ID',
  'apa.noGrants': 'No active grants.',
  'apa.colAgent': 'Agent',
  'apa.colOwner': 'Owner',
  'apa.colDept': 'Department',
  'apa.disable': 'Disable',
  'apa.enable': 'Enable',
  'apa.exposeWeb': 'Safe to expose web results',
  'apa.noAgents': 'No agents.',
  'apa.budgetScope': 'Scope',
  'apa.budgetScopeId': 'ID',
  'apa.budgetUsd': 'Monthly limit (USD)',
  'apa.budgetTokens': 'Monthly limit (tokens)',
  'apa.budgetSave': 'Save budget',
  'apa.noBudgets': 'No budgets set.',
  'apa.auditVerify': 'Verify chain',
  'apa.auditIntact': 'Audit chain intact.',
  'apa.auditBroken': 'Audit chain broken at event #{id}.',
  'apa.colType': 'Event',
  'apa.colDetail': 'Detail',
  'apa.usageModel': 'Model',
  'apa.usageCalls': 'Calls',
  'apa.usageCost': 'Cost (USD)',
  'apa.usageMargin': 'Calls in the 85K–100K safety band',
  'apa.noUsage': 'No model calls this month.',
  'apa.noAudit': 'No audit events yet.',
  'apa.scope.User': 'User',
  'apa.scope.Agent': 'Agent',
  'apa.scope.Tenant': 'Department',
};

const ar: Record<keyof typeof en, string> = {
  'nav.agentBuilder': 'منشئ الوكلاء',
  'nav.agentPlatform': 'منصة الوكلاء',

  'ap.title': 'منشئ الوكلاء',
  'ap.subtitle': 'أنشئ وكلاء من التعليمات والأدوات المعتمدة. تختار المنصة النموذج وتطبّق صلاحياتك.',
  'ap.loadFailed': 'تعذّر تحميل وكلائك.',
  'ap.new': 'وكيل جديد',
  'ap.mine': 'وكلائي',
  'ap.shared': 'مشترك معي',
  'ap.empty': 'لا يوجد وكلاء بعد.',
  'ap.loading': 'جارٍ تحميل منشئ الوكلاء…',
  'ap.noAccess': 'منشئ الوكلاء غير مفعّل لحسابك. اطلب الصلاحية من المسؤول.',

  'ap.admin.section': 'منشئ الوكلاء',
  'ap.admin.enable': 'السماح لهذا المستخدم بإنشاء الوكلاء وتشغيلها',
  'ap.admin.hint': 'معطّل افتراضيًا. يسري من طلبه التالي، بما في ذلك عمليات التشغيل الجارية.',
  'ap.admin.adminNote': 'لدى المسؤولين دائمًا صلاحية الوصول إلى منشئ الوكلاء.',
  'ap.admin.updateFailed': 'تعذّر تحديث صلاحية منشئ الوكلاء.',
  'ap.disabled': 'أوقفه أحد المسؤولين',
  'ap.version': 'الإصدار {n}',

  'ap.edit.open': 'تعديل',
  'ap.edit.name': 'الاسم',
  'ap.edit.instructions': 'التعليمات',
  'ap.edit.instructionsHint': 'ما الذي يفعله الوكيل وكيف. النماذج والحدود والصلاحيات تحددها المنصة.',
  'ap.edit.tools': 'الأدوات',
  'ap.edit.toolsHint': 'لا يمكن إرفاق إلا الأدوات المعتمدة من المسؤول والمتاحة لك.',
  'ap.edit.noTools': 'لا توجد أدوات معتمدة بعد.',
  'ap.edit.unavailable': 'غير ممنوحة لك',
  'ap.edit.web': 'الويب',
  'ap.edit.writes': 'يغيّر البيانات',
  'ap.edit.agents': 'وكلاء آخرون يمكنه استدعاؤهم',
  'ap.edit.share': 'مشاركة مع قسمي',
  'ap.edit.memory': 'تذكّر المحادثات',
  'ap.edit.retention': 'مدة الحفظ (أيام)',
  'ap.edit.save': 'حفظ',
  'ap.edit.saving': 'جارٍ الحفظ…',
  'ap.edit.cancel': 'إلغاء',
  'ap.edit.saved': 'حُفظ كإصدار {n}.',
  'ap.edit.rejected': 'رفضت المنصة هذا الوكيل:',
  'ap.edit.versions': 'الإصدارات',
  'ap.edit.rollback': 'استعادة',

  'ap.run.placeholder': 'اكتب رسالة للوكيل…',
  'ap.run.send': 'إرسال',
  'ap.run.sending': 'جارٍ العمل…',
  'ap.run.handledBy': 'أجاب عنه {model}',
  'ap.run.usedWeb': 'استخدم محتوى من الويب',
  'ap.run.failed': 'تعذّر على الوكيل الإجابة.',

  'ap.web.have': 'لديك صلاحية الوصول إلى الويب.',
  'ap.web.none': 'ليست لديك صلاحية الوصول إلى الويب.',
  'ap.web.request': 'طلب الوصول إلى الويب',
  'ap.web.reason': 'لماذا تحتاج إليه؟',
  'ap.web.pending': 'طلبك بانتظار موافقة المسؤول.',
  'ap.web.sent': 'تم إرسال الطلب.',

  'apa.title': 'منصة الوكلاء',
  'apa.subtitle': 'امنح الصلاحيات ووافق على طلبات الويب وأوقف الوكلاء وحدّد الميزانيات وراجع سجل التدقيق. النماذج والتوجيه يغيّرها مشغّلو المنصة فقط.',
  'apa.tab.requests': 'طلبات الويب',
  'apa.tab.entitlements': 'الصلاحيات',
  'apa.tab.agents': 'الوكلاء',
  'apa.tab.budgets': 'الميزانيات',
  'apa.tab.audit': 'التدقيق',
  'apa.tab.usage': 'الاستخدام',
  'apa.tab.domains': 'نطاقات الويب',
  'apa.domainsDesc': 'قيّد البحث على الويب لقسم واحد. يمكن فقط تضييق قائمة المنصة، ولا يمكن توسيعها.',
  'apa.domainsInclude': 'هذه النطاقات فقط (نطاق في كل سطر؛ فارغ = أي نطاق تسمح به المنصة)',
  'apa.domainsExclude': 'استبعد هذه النطاقات دائمًا (نطاق في كل سطر)',
  'apa.domainsLoad': 'تحميل',
  'apa.domainsSave': 'حفظ النطاقات',
  'apa.domainsSaved': 'تم الحفظ.',
  'apa.loadFailed': 'تعذّر تحميل هذا القسم.',
  'apa.colUser': 'المستخدم',
  'apa.colReason': 'السبب',
  'apa.colWhen': 'الوقت',
  'apa.colStatus': 'الحالة',
  'apa.approve': 'موافقة',
  'apa.reject': 'رفض',
  'apa.noRequests': 'لا توجد طلبات معلّقة.',
  'apa.grant': 'منح',
  'apa.revoke': 'سحب',
  'apa.entName': 'الصلاحية',
  'apa.entTarget': 'مستخدم أو قسم',
  'apa.entUserId': 'رقم المستخدم',
  'apa.entDeptId': 'رقم القسم',
  'apa.noGrants': 'لا توجد صلاحيات فعّالة.',
  'apa.colAgent': 'الوكيل',
  'apa.colOwner': 'المالك',
  'apa.colDept': 'القسم',
  'apa.disable': 'إيقاف',
  'apa.enable': 'تفعيل',
  'apa.exposeWeb': 'آمن لعرض نتائج الويب',
  'apa.noAgents': 'لا يوجد وكلاء.',
  'apa.budgetScope': 'النطاق',
  'apa.budgetScopeId': 'المعرّف',
  'apa.budgetUsd': 'الحد الشهري (دولار)',
  'apa.budgetTokens': 'الحد الشهري (رموز)',
  'apa.budgetSave': 'حفظ الميزانية',
  'apa.noBudgets': 'لم تُحدَّد ميزانيات.',
  'apa.auditVerify': 'التحقق من السلسلة',
  'apa.auditIntact': 'سلسلة التدقيق سليمة.',
  'apa.auditBroken': 'سلسلة التدقيق مكسورة عند الحدث رقم {id}.',
  'apa.colType': 'الحدث',
  'apa.colDetail': 'التفاصيل',
  'apa.usageModel': 'النموذج',
  'apa.usageCalls': 'الاستدعاءات',
  'apa.usageCost': 'التكلفة (دولار)',
  'apa.usageMargin': 'استدعاءات في نطاق الأمان 85 ألف–100 ألف',
  'apa.noUsage': 'لا توجد استدعاءات للنماذج هذا الشهر.',
  'apa.noAudit': 'لا توجد أحداث تدقيق بعد.',
  'apa.scope.User': 'مستخدم',
  'apa.scope.Agent': 'وكيل',
  'apa.scope.Tenant': 'قسم',
};

const agentPlatform = { en, ar };
export default agentPlatform;
